import type { ChartType, ColumnDefinition, DataRow } from "@compass/shared/schema/chart";
import { enqueueSnackbar } from "notistack";
import type React from "react";
import { useState } from "react";
import { DataChart } from "@compass/shared/components/charts/DataChart";
import { DataTable } from "@compass/shared/components/charts/DataTable";
import { useSpreadsheetImport } from "@compass/shared/components/charts/useSpreadsheetImport";
import { Button } from "components/elements";

/* ─── Expand Toggle ─── */

export const ExpandToggle: React.FC<{ expanded: boolean; onToggle: () => void }> = ({
    expanded,
    onToggle,
}) => (
    <button
        onClick={onToggle}
        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        title={expanded ? "Collapse" : "Expand"}
    >
        {expanded ? (
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 9L4 4m0 0v5m0-5h5m6 6l5 5m0 0v-5m0 5h-5"
                />
            </svg>
        ) : (
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"
                />
            </svg>
        )}
    </button>
);

/* ─── Chart Type Switcher ─── */

export const ChartTypeSwitcher: React.FC<{
    value: ChartType;
    onChange: (type: ChartType) => void;
}> = ({ value, onChange }) => (
    <div className="mt-3 flex gap-1">
        {(["LINE", "BAR"] as const).map((type) => (
            <button
                key={type}
                onClick={() => onChange(type)}
                className={`rounded px-3 py-1 text-sm ${
                    value === type ? "bg-indigo-500 text-white" : "bg-gray-100 hover:bg-gray-200"
                }`}
            >
                {type}
            </button>
        ))}
    </div>
);

/* ─── Chart Preview Card ─── */

export const ChartPreviewCard: React.FC<{
    columns: ColumnDefinition[];
    rows: DataRow[];
    chartType: ChartType;
    onChartTypeChange: (type: ChartType) => void;
    expandedPreview: boolean;
    onToggleExpand: () => void;
}> = ({ columns, rows, chartType, onChartTypeChange, expandedPreview, onToggleExpand }) => (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Preview</h3>
            <ExpandToggle expanded={expandedPreview} onToggle={onToggleExpand} />
        </div>
        <DataChart columns={columns} rows={rows} chartType={chartType} />
        <ChartTypeSwitcher value={chartType} onChange={onChartTypeChange} />
    </div>
);

/* ─── Expanded Preview Overlay ─── */

export const ExpandedPreviewOverlay: React.FC<{
    columns: ColumnDefinition[];
    rows: DataRow[];
    chartType: ChartType;
    onChartTypeChange: (type: ChartType) => void;
    onClose: () => void;
}> = ({ columns, rows, chartType, onChartTypeChange, onClose }) => (
    <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-8"
        onClick={onClose}
    >
        <div
            className="flex h-full max-h-[90vh] w-full max-w-6xl flex-col rounded-xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Preview</h2>
                <ExpandToggle expanded onToggle={onClose} />
            </div>
            <div className="min-h-0 flex-1">
                <DataChart columns={columns} rows={rows} chartType={chartType} />
            </div>
            <ChartTypeSwitcher value={chartType} onChange={onChartTypeChange} />
        </div>
    </div>
);

/* ─── Inputs Card ─── */

const ALLOWED_EXTENSIONS = [".xls", ".xlsx", ".csv"];
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export const InputsCard: React.FC<{
    columns: ColumnDefinition[];
    rows: DataRow[];
    onChange: (cols: ColumnDefinition[], rows: DataRow[]) => void;
    readOnly?: boolean;
    exportFilename?: string;
    statusText?: React.ReactNode;
    actions?: React.ReactNode;
}> = ({ columns, rows, onChange, readOnly, exportFilename, statusText, actions }) => {
    const { handleInputChange: handleImportInput } = useSpreadsheetImport(
        (newCols, newRows) => {
            onChange(newCols, newRows);
            enqueueSnackbar("Data imported successfully", { variant: "success" });
        },
        (message) => {
            enqueueSnackbar(message, { variant: "error" });
        },
    );

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const ext = "." + file.name.split(".").pop()?.toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            enqueueSnackbar("Only XLS, XLSX, and CSV files can be imported.", { variant: "error" });
            e.target.value = "";
            return;
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
            enqueueSnackbar(
                `File exceeds the ${MAX_FILE_SIZE_MB}MB size limit. Please upload a smaller file.`,
                { variant: "error" },
            );
            e.target.value = "";
            return;
        }

        handleImportInput(e);
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold">Inputs</h3>
                {statusText}
            </div>

            <DataTable
                columns={columns}
                rows={rows}
                onChange={onChange}
                onPasteData={!readOnly ? onChange : undefined}
                readOnly={readOnly}
                exportFilename={exportFilename}
            />

            {!readOnly && (
                <div className="mt-3 flex gap-2">
                    <label className="inline-flex cursor-pointer items-center rounded border px-3 py-1.5 text-sm hover:bg-gray-50">
                        Import File
                        <input
                            type="file"
                            accept=".xls,.xlsx,.csv"
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                        />
                    </label>
                    {actions}
                </div>
            )}
        </div>
    );
};

/* ─── Details Form Card ─── */

export const DetailsFormCard: React.FC<{
    title: string;
    description: string;
    onTitleChange: (value: string) => void;
    onDescriptionChange: (value: string) => void;
    heading?: string;
    titleRequired?: boolean;
    actions?: React.ReactNode;
}> = ({
    title,
    description,
    onTitleChange,
    onDescriptionChange,
    heading = "Details",
    titleRequired,
    actions,
}) => (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold">{heading}</h3>
        <div className="space-y-3">
            <div>
                <label className="mb-1 block text-xs font-medium text-gray-500">
                    Title{titleRequired ? " *" : ""}
                </label>
                <input
                    type="text"
                    value={title}
                    onChange={(e) => onTitleChange(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder="Enter title"
                />
            </div>
            <div>
                <label className="mb-1 block text-xs font-medium text-gray-500">Description</label>
                <textarea
                    value={description}
                    onChange={(e) => onDescriptionChange(e.target.value)}
                    rows={2}
                    className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder="Optional"
                />
            </div>
            {actions && <div className="flex gap-2">{actions}</div>}
        </div>
    </div>
);

/* ─── Permissions Summary Card ─── */

export interface PermissionSummaryEntry {
    key: string;
    label: string;
    role: string;
}

export const PermissionsSummaryCard: React.FC<{
    entries: PermissionSummaryEntry[];
    summaryText: string;
    onManage?: () => void;
    collapsible?: boolean;
}> = ({ entries, summaryText, onManage, collapsible = true }) => {
    const [expanded, setExpanded] = useState(!collapsible);

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            {collapsible ? (
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="flex w-full items-center justify-between text-left"
                >
                    <h3 className="text-sm font-semibold">Permissions</h3>
                    <svg
                        className={`h-4 w-4 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                </button>
            ) : (
                <h3 className="mb-3 text-sm font-semibold">Permissions</h3>
            )}
            <p className="mt-1 text-xs text-gray-500">{summaryText}</p>
            {expanded && entries.length > 0 && (
                <div className="mt-3 space-y-2 border-t pt-3">
                    {entries.map((e) => (
                        <div key={e.key} className="flex items-center justify-between text-sm">
                            <span>{e.label}</span>
                            <span className="text-xs text-gray-500">{e.role}</span>
                        </div>
                    ))}
                    {onManage && (
                        <Button variant="outline" className="mt-2 w-full" onClick={onManage}>
                            Manage Permissions
                        </Button>
                    )}
                </div>
            )}
            {!collapsible && entries.length === 0 && onManage && (
                <Button variant="outline" className="mt-3 w-full" onClick={onManage}>
                    Manage Permissions
                </Button>
            )}
        </div>
    );
};
